import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { deliveryFailureReason } from "@/lib/replyWindow";

// Shown above the reply box when the customer's 24h reply window has closed,
// so the owner knows why they can't send instead of a reply silently vanishing.
export function ReplyWindowNotice({
  channelLabel,
  lastCustomerAt,
  children,
}: {
  channelLabel: string;
  lastCustomerAt: Date | null;
  /** e.g. the template picker, on channels that have templates. */
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <div role="status">
        <p className="font-medium text-foreground">Reply window closed</p>
        <p className="mt-0.5 leading-relaxed">
          {channelLabel} only allows replies within 24 hours of the customer's last message.{" "}
          {lastCustomerAt
            ? `Theirs was at ${format(lastCustomerAt, "h:mm a, MMM d")}. `
            : "They haven't messaged you here yet. "}
          You can reply as soon as they message you.
        </p>
      </div>
      {children}
    </div>
  );
}

// Inside a sent bubble: the provider accepted the message but later reported
// it as not delivered (ai_messages.delivery_failed_at / delivery_error).
export function DeliveryFailedNote({ error, className }: { error: unknown; className?: string }) {
  return (
    <p className={cn("mt-1 flex items-start gap-1 text-[11px] font-medium", className)}>
      <AlertCircle className="mt-px h-3 w-3 shrink-0" />
      <span>Not delivered: {deliveryFailureReason(error)}</span>
    </p>
  );
}
